import Select, { SelectChangeEvent } from '@mui/material/Select';

import FormControl from '@mui/material/FormControl';
import { ISearchFilter } from '@orcha/types/search';
import InputLabel from '@mui/material/InputLabel';
import { ListSubheader } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import React from 'react';
import { logEventAnalytic } from 'src/utilities/logAnalytics';
import { styled } from '@mui/material/styles';
import theme from 'src/themes';
import useTranslation from 'next-translate/useTranslation';
import FilterMenuItem from '../../FilterMenuItem';

const StyledFormControl = styled(FormControl)(() => ({
  backgroundColor: theme.palette.common.white,
}));

const StyledListSubheader = styled(ListSubheader)(() => ({
  color: theme.palette.common.black,
}));

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 400,
    },
  },
};

type GroupedArrayFilterProps = {
  filter: ISearchFilter;
  selectedOptions: string[];
  handleSelectedChange: (selectedOptions: string[]) => void;
};

export const GroupedArrayFilter = (props: GroupedArrayFilterProps) => {
  const { filter, selectedOptions, handleSelectedChange } = props;
  const { t } = useTranslation('search');

  if (filter.dataType !== 'GroupedArray') {
    return <></>;
  }

  const getRenderValue = (selected: string[]) => {
    if (filter.isValueTranslationRequired) {
      return selected
        .map((value) =>
          t(
            `filterOption.${filter.filterName.toLocaleLowerCase()}.${value.toLocaleLowerCase()}`
          )
        )
        .join(', ');
    }
    return selected.join(', ');
  };
  const handleChange = (event: SelectChangeEvent<typeof selectedOptions>) => {
    const {
      target: { value },
    } = event;
    const label = t(`filterLabel.${filter.filterName.toLowerCase()}`);
    logEventAnalytic({
      category: 'Filters',
      action: 'Filter clicked on search page - microsite',
      label: `${value} - ${label}`,
    });
    handleSelectedChange(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <StyledFormControl fullWidth size="small">
      <InputLabel id={`filter-label-${filter.filterName}`}>
        {t(`filterLabel.${filter.filterName.toLowerCase()}`)}
      </InputLabel>
      <Select
        labelId={`filter-label-${filter.filterName}`}
        multiple
        value={selectedOptions}
        onChange={handleChange}
        label={filter.filterName}
        renderValue={getRenderValue}
        variant="outlined"
        MenuProps={MenuProps}
        data-testid={`filter-select-${filter.filterName.replace(
          /[A-Z]/g,
          (m) => `-${m.toLowerCase()}`
        )}`}
      >
        {filter.filterValues
          .sort((a, b) => (a.filterName > b.filterName ? 1 : -1))
          ?.filter((g) => g.filterValues && g.filterValues.length > 0)
          .map((group) => {
            return [
              <StyledListSubheader key={group.filterName}>
                {group.filterName}
              </StyledListSubheader>,
              group.filterValues?.map((option) => {
                return (
                  <MenuItem key={option} value={option}>
                    <FilterMenuItem
                      key={option}
                      filterName={filter.filterName}
                      option={option}
                      isChecked={selectedOptions.indexOf(option) > -1}
                      requiresTranslation={filter.isValueTranslationRequired}
                    />
                  </MenuItem>
                );
              }),
            ];
          })}
      </Select>
    </StyledFormControl>
  );
};
